import { LocalDate } from "@js-joda/core";
import type { CompanyGigShift } from "model/Gig";
import type { StaffProfileData } from "services/api/company/staff.service";
import { getStockholmLocalDateNow } from "web-apps/company/utils/timeAndDate";
import type { SelectedWorker } from "../modal/gigFormModal.types";
import { shiftsOverlap } from "./shiftForm.logic";

export interface PeriodRanges {
  copyFromDates: LocalDate[];
  createInDates: LocalDate[];
}

export interface CopyScheduleSuccessResult {
  addedShiftCount: number;
}

const toDraftShift = (shift: CompanyGigShift): CompanyGigShift => ({
  ...shift,
  id: undefined,
  gigId: undefined,
});

const getNonOverlappingShifts = ({
  copiedShifts,
  existingShifts,
}: {
  copiedShifts: CompanyGigShift[];
  existingShifts: CompanyGigShift[];
}): CompanyGigShift[] => {
  const acceptedShifts: CompanyGigShift[] = [...existingShifts];

  return copiedShifts.filter((copiedShift) => {
    const hasOverlap = acceptedShifts.some((existingShift) =>
      shiftsOverlap(existingShift, copiedShift),
    );

    if (hasOverlap) {
      return false;
    }

    acceptedShifts.push(copiedShift);
    return true;
  });
};

/** Builds default source and target date ranges when no shifts exist yet. */
export const getDefaultPeriodRanges = (): PeriodRanges => {
  const today = getStockholmLocalDateNow();
  const copyFromEnd = today;
  const copyFromStart = copyFromEnd.minusWeeks(4).plusDays(1);
  const createInStart = today.plusDays(1);
  const createInEnd = createInStart.plusWeeks(4).minusDays(1);

  return {
    copyFromDates: [copyFromStart, copyFromEnd],
    createInDates: [createInStart, createInEnd],
  };
};

/** Maps a loaded staff profile into the worker shape used by the modal flow. */
export const mapStaffProfileToModalWorker = ({
  workerId,
  workerProfile,
}: {
  workerId: number;
  workerProfile: StaffProfileData;
}): SelectedWorker => {
  const externalId = workerProfile.externalId;

  return {
    firstName: workerProfile.firstName ?? "",
    lastName: workerProfile.lastName ?? "",
    personalIdentityNumber: workerProfile.personalIdentityNumber ?? "",
    workGroups: workerProfile.workGroups.map((workGroup) => ({
      id: workGroup.id,
      name: workGroup.name ?? "",
      workGroupId: workGroup.id,
      workGroupName: workGroup.name ?? "",
      companyUnitId: workGroup.companyUnitId,
      companyUnitName: workGroup.companyUnitName,
    })),
    employmentInfo: {
      current: {
        endDate: workerProfile.employmentInfo?.current?.endDate ?? null,
        employmentType:
          workerProfile.employmentInfo?.current?.employmentType ?? "",
        paymentType: workerProfile.employmentInfo?.current?.paymentType ?? "",
        startDate: workerProfile.employmentInfo?.current?.startDate ?? null,
      },
      employments: workerProfile.employments.map((employment) => ({
        endDate: employment.endDate,
        startDate: employment.startDate,
      })),
    },
    id: workerId,
    workerId,
    externalId: externalId ?? "",
  };
};

/** Merges copied shifts using the shared non-overlap rule and reports the result. */
export const mergeCopiedShifts = ({
  copiedShifts,
  existingShifts,
}: {
  copiedShifts: CompanyGigShift[];
  existingShifts: CompanyGigShift[];
}): {
  acceptedShifts: CompanyGigShift[];
  addedShiftCount: number;
} => {
  if (copiedShifts.length === 0) {
    return {
      acceptedShifts: [],
      addedShiftCount: 0,
    };
  }

  const draftCopiedShifts = copiedShifts.map(toDraftShift);
  const acceptedShifts = getNonOverlappingShifts({
    existingShifts,
    copiedShifts: draftCopiedShifts,
  });

  return {
    acceptedShifts,
    addedShiftCount: acceptedShifts.length,
  };
};
